<template>
  <SectionFrame v-if="groups.length || inStudio" :section="section" :index="index" :heading="heading" label="Prices" :narrow="layout === 'menu'">
    <template v-if="groups.length">
      <!-- Menu: dotted leaders, like a café board or a salon price list. -->
      <div v-if="layout === 'menu'" class="s2-price-menu">
        <div v-for="g in groups" :key="g.name" class="s2-price-group mo-reveal">
          <h3 v-if="g.name" class="t-h3">{{ g.name }}</h3>
          <ul>
            <li v-for="item in g.items" :key="item.name">
              <span class="s2-price-name">{{ item.name }}<span v-if="section.showNotes !== false && item.note" class="s2-muted s2-small"> · {{ item.note }}</span></span>
              <span class="s2-leader" aria-hidden="true"></span>
              <span class="t-price">{{ priceLabel(item) }}<sup v-if="note">{{ note }}</sup></span>
            </li>
          </ul>
        </div>
      </div>
      <table v-else-if="layout === 'table'" class="s2-price-table t-body mo-reveal">
        <caption class="s2-hp">{{ heading || "Prices" }}</caption>
        <template v-for="g in groups" :key="g.name">
          <thead v-if="g.name"><tr><th colspan="2" scope="colgroup" class="t-h3">{{ g.name }}</th></tr></thead>
          <tbody>
          <tr v-for="item in g.items" :key="item.name">
            <th scope="row">{{ item.name }}<div v-if="section.showNotes !== false && item.note" class="s2-muted s2-small">{{ item.note }}</div></th>
            <td class="t-price">{{ priceLabel(item) }}<sup v-if="note">{{ note }}</sup></td>
          </tr>
          </tbody>
        </template>
      </table>
      <div v-else class="s2-grid mo-stagger" style="--s2-cols: 3">
        <div v-for="g in groups" :key="g.name" class="t-card">
          <h3 v-if="g.name" class="t-h3">{{ g.name }}</h3>
          <ul class="s2-price-cardlist">
            <li v-for="item in g.items" :key="item.name"><span>{{ item.name }}</span><span class="t-price">{{ priceLabel(item) }}</span></li>
          </ul>
        </div>
      </div>
    </template>
    <StudioGap v-else>Add your prices (type them in, or show them from your products). Draksy never guesses prices.</StudioGap>
  </SectionFrame>
</template>

<script setup>
import {computed} from "vue";
import SectionFrame from "./SectionFrame.vue";
import StudioGap from "./StudioGap.vue";
import {layoutOf, priceGroups} from "@/utils/sectionLogic.js";
import {studioMode} from "@/classes/previewBridge.js";
import {useFootnotes} from "@/stores/footnotes.js";

const props = defineProps({section: {type: Object, required: true}, index: {type: Number, default: 0}, data: {type: Object, default: () => ({})}});
const inStudio = computed(() => studioMode.value !== "off");
const layout = computed(() => layoutOf(props.section, ["menu", "table", "cards"]));
const heading = computed(() => props.section.heading ?? "Prices");
const groups = computed(() => priceGroups(props.section, props.data.products));
// "from" only where the business marked an item as a starting price.
const priceLabel = (item) => (item.price ? `${item.from && props.section.showFrom !== false ? "from " : ""}${item.price}` : "Ask us");
// Prices from products are shown including VAT: say so once, in the footer.
const note = props.section.fromProducts && groups.value.length ? useFootnotes().add("Prices include VAT where it applies.") : null;
</script>

<style>
.s2-price-group + .s2-price-group { margin-top: 2rem; }
.s2-price-menu ul { list-style: none; margin: .75rem 0 0; padding: 0; }
.s2-price-menu li { display: flex; align-items: baseline; gap: .6rem; padding: .45rem 0; }
.s2-price-name { flex: 0 1 auto; }
.s2-leader { flex: 1 1 2rem; border-bottom: 2px dotted var(--t-line); transform: translateY(-.25em); min-width: 1rem; }
.s2-price-table { width: 100%; border-collapse: collapse; }
.s2-price-table th, .s2-price-table td { text-align: left; padding: .6rem 0; border-bottom: 1px solid var(--t-line); font-weight: 400; vertical-align: top; }
.s2-price-table thead th { padding-top: 1.5rem; border-bottom: 2px solid var(--t-ink); }
.s2-price-table td { text-align: right; white-space: nowrap; }
.s2-price-cardlist { list-style: none; margin: .75rem 0 0; padding: 0; }
.s2-price-cardlist li { display: flex; justify-content: space-between; gap: 1rem; padding: .4rem 0; border-bottom: 1px solid var(--t-line); }
</style>
